import React, { useState } from 'react'
import { Link } from 'react-router-dom'
import s from './FAQPage.module.css'
import layout from './LandingPage.module.css'

interface FAQItem {
  q: string
  a: React.ReactNode
  note?: string
}

interface FAQCategory {
  title: string
  icon: string
  items: FAQItem[]
}

const check = <span className={s.tableYes}>&#10003;</span>
const dash = <span className={s.tableNo}>&#8211;</span>

const faqData: FAQCategory[] = [
  {
    title: 'Getting Started',
    icon: '\u25B6',
    items: [
      {
        q: 'What is AllocatIQ and who is it built for?',
        a: (
          <p className={s.answerP}>
            AllocatIQ is a resource forecasting and utilization platform built specifically for professional services teams — consulting firms, IT services companies, managed service providers, digital agencies, and PMO offices. It replaces scattered spreadsheets with a single workspace for Gantt chart scheduling, revenue forecasting, profitability analysis, and real-time resource utilization tracking.
          </p>
        ),
      },
      {
        q: 'How does the 14-day free trial work?',
        a: (
          <ul className={s.answerList}>
            <li>Full access to Professional plan features for 14 days</li>
            <li>No credit card required to start</li>
            <li>At the end of the trial, choose a paid plan (Starter, Professional, or Enterprise)</li>
            <li>If you do not select a plan, your organization enters a read-only state until you subscribe</li>
          </ul>
        ),
      },
      {
        q: 'Do I need a credit card to start my free trial?',
        a: (
          <p className={s.answerP}>
            No. You can sign up and use the full platform for 14 days without entering any payment information. You only need to provide payment details when you are ready to select a paid plan.
          </p>
        ),
      },
      {
        q: 'How long does it take to set up AllocatIQ for my team?',
        a: (
          <p className={s.answerP}>
            Most teams are up and running within 30 minutes. After creating your account, a guided onboarding flow helps you set your organization name and currency preferences. From there, you can import resources, projects, and allocations via bulk CSV upload, or enter them manually. The interface is designed to be intuitive for anyone who has used project management or spreadsheet tools before.
          </p>
        ),
      },
      {
        q: 'Can I import my existing data from spreadsheets?',
        a: (
          <ul className={s.answerList}>
            <li>Bulk CSV uploads are supported for resources, projects, allocations, and other key entities</li>
            <li>Download a template from the bulk upload modal within each section</li>
            <li>Fill in the template and upload it to bring your existing data into the platform quickly</li>
          </ul>
        ),
      },
      {
        q: 'What happens to my data if I do not subscribe after the trial?',
        a: (
          <ul className={s.answerList}>
            <li>Your data is preserved during the 14-day trial and a 14-day read-only grace period that follows</li>
            <li>After the grace period expires, your organization is suspended and data is eventually permanently deleted</li>
            <li>You can export your data at any time before deletion</li>
            <li>We recommend exporting if you decide not to continue</li>
          </ul>
        ),
      },
    ],
  },
  {
    title: 'Features & Functionality',
    icon: '\u2699',
    items: [
      {
        q: 'What does the Gantt chart scheduling feature do?',
        a: (
          <p className={s.answerP}>
            The Gantt chart provides a visual timeline of all resource allocations across your projects. You can switch between day, week, month, quarter, and year views. Allocations are color-coded by utilization level — overbooked, full (8h), partial, and non-billable — so you can see capacity at a glance and adjust assignments by dragging and dropping.
          </p>
        ),
      },
      {
        q: 'How does revenue forecasting work?',
        a: (
          <p className={s.answerP}>
            Revenue forecasting projects your revenue over a rolling 6-month period based on current allocations, billable rates, and project timelines. You can view projections by project, by resource, or across the entire organization. Multi-currency support with live exchange rates means you see accurate figures regardless of which currencies your projects operate in.
          </p>
        ),
      },
      {
        q: 'What is "Forecast vs Actual" and why does it matter?',
        a: (
          <p className={s.answerP}>
            Forecast vs Actual compares your planned resource allocations against the real time logged in timesheets. This lets you spot variances early — for example, a resource allocated at 80% but only logging 50% — so you can course-correct before those variances become budget problems. It is one of the most valuable reports for professional services profitability.
          </p>
        ),
      },
      {
        q: 'What does profitability analysis show me?',
        a: (
          <p className={s.answerP}>
            Profitability analysis calculates per-project P&amp;L by comparing the revenue generated (based on billable rates and logged hours) against the cost of the resources assigned. You can see margins by project, by customer, and by resource, helping you identify which engagements drive the most value and which may be eroding margin.
          </p>
        ),
      },
      {
        q: 'How does resource utilization tracking work?',
        a: (
          <p className={s.answerP}>
            AllocatIQ tracks capacity, over-booking, and bench time for every resource in your organization. The dashboard shows utilization percentages, available hours, and unallocated time. You can filter by department, role type, or individual resource to understand where your team is fully booked and where you have capacity to take on new work.
          </p>
        ),
      },
      {
        q: 'How do timesheets and approvals work?',
        a: (
          <ul className={s.answerList}>
            <li>Resources log time against their allocations using a weekly timesheet interface</li>
            <li>Each entry records hours, a description, and the date</li>
            <li>Submitted timesheets go to a pending state for approval</li>
            <li>Project Managers or Org Admins can approve or reject timesheets</li>
            <li>Once approved, a timesheet is locked and cannot be edited</li>
            <li>Only the resource who owns the timesheet can create and edit entries while pending</li>
          </ul>
        ),
      },
      {
        q: 'What are partner rate sheets and how do I use them?',
        a: (
          <p className={s.answerP}>
            Partner rate sheets let you define standard billing rates for partner organizations or subcontractors. When you create an allocation with a billable rate, you can reference a partner rate sheet to ensure consistent pricing across engagements. This is available on the Professional and Enterprise plans.
          </p>
        ),
      },
      {
        q: 'Does AllocatIQ support multiple currencies?',
        a: (
          <p className={s.answerP}>
            Yes. You can set a currency for each project budget and each allocation rate. The platform supports major ISO 4217 currency codes (USD, EUR, GBP, and more). Revenue forecasts and profitability reports aggregate across currencies, giving you a consolidated financial view.
          </p>
        ),
      },
      {
        q: 'What are departments and how do they help?',
        a: (
          <p className={s.answerP}>
            Departments let you group resources by team, function, or cost center. You can filter the Gantt chart, utilization reports, and allocation views by department to understand capacity and workload for each part of your organization. Department management is available on all plans.
          </p>
        ),
      },
      {
        q: 'Can I manage holidays and time off?',
        a: (
          <p className={s.answerP}>
            Yes. You can define organizational holidays that affect capacity calculations, and resources can request time off. The system factors these into utilization and availability views so your forecasts reflect real working time, not just raw calendar days.
          </p>
        ),
      },
      {
        q: 'Which features are available on which plans?',
        a: (
          <table className={s.answerTable}>
            <thead>
              <tr>
                <th>Feature</th>
                <th>Starter</th>
                <th>Professional</th>
                <th>Enterprise</th>
              </tr>
            </thead>
            <tbody>
              <tr><td>Gantt scheduling</td><td>{check}</td><td>{check}</td><td>{check}</td></tr>
              <tr><td>Resource utilization</td><td>{check}</td><td>{check}</td><td>{check}</td></tr>
              <tr><td>Timesheets &amp; approvals</td><td>{check}</td><td>{check}</td><td>{check}</td></tr>
              <tr><td>Departments &amp; holidays</td><td>{check}</td><td>{check}</td><td>{check}</td></tr>
              <tr><td>Basic reports</td><td>{check}</td><td>{check}</td><td>{check}</td></tr>
              <tr><td>Revenue forecasting</td><td>{check}</td><td>{check}</td><td>{check}</td></tr>
              <tr><td>Partner rate sheets</td><td>{dash}</td><td>{check}</td><td>{check}</td></tr>
              <tr><td>Advanced reporting</td><td>{dash}</td><td>{check}</td><td>{check}</td></tr>
              <tr><td>Audit logging</td><td>{dash}</td><td>{check}</td><td>{check}</td></tr>
              <tr><td>API access</td><td>{dash}</td><td>{check}</td><td>{check}</td></tr>
              <tr><td>SSO (planned)</td><td>{dash}</td><td>{dash}</td><td>{check}</td></tr>
              <tr><td>Unlimited users, projects, resources</td><td>{dash}</td><td>{dash}</td><td>{check}</td></tr>
            </tbody>
          </table>
        ),
      },
    ],
  },
  {
    title: 'Pricing & Plans',
    icon: '\uD83D\uDCB0',
    items: [
      {
        q: 'What are the three pricing plans?',
        a: (
          <table className={s.answerTable}>
            <thead>
              <tr>
                <th></th>
                <th>Starter</th>
                <th>Professional</th>
                <th>Enterprise</th>
              </tr>
            </thead>
            <tbody>
              <tr><td className={s.answerTableLabel}>Price</td><td>$79/mo</td><td>$199/mo</td><td>$499/mo</td></tr>
              <tr><td className={s.answerTableLabel}>Users</td><td>Up to 10</td><td>Up to 50</td><td>Unlimited</td></tr>
              <tr><td className={s.answerTableLabel}>Projects</td><td>Up to 10</td><td>Up to 50</td><td>Unlimited</td></tr>
              <tr><td className={s.answerTableLabel}>Resources</td><td>Up to 25</td><td>Up to 100</td><td>Unlimited</td></tr>
              <tr><td className={s.answerTableLabel}>Revenue forecasting</td><td>{check}</td><td>{check}</td><td>{check}</td></tr>
              <tr><td className={s.answerTableLabel}>Partner rate sheets</td><td>{dash}</td><td>{check}</td><td>{check}</td></tr>
              <tr><td className={s.answerTableLabel}>Advanced reporting</td><td>{dash}</td><td>{check}</td><td>{check}</td></tr>
              <tr><td className={s.answerTableLabel}>Audit logging</td><td>{dash}</td><td>{check}</td><td>{check}</td></tr>
              <tr><td className={s.answerTableLabel}>API access</td><td>{dash}</td><td>{check}</td><td>{check}</td></tr>
              <tr><td className={s.answerTableLabel}>SSO (planned)</td><td>{dash}</td><td>{dash}</td><td>{check}</td></tr>
              <tr><td className={s.answerTableLabel}>Support</td><td>Email</td><td>Priority email</td><td>Dedicated</td></tr>
            </tbody>
          </table>
        ),
      },
      {
        q: 'How does user-based pricing work?',
        a: (
          <ul className={s.answerList}>
            <li>Each plan has a maximum number of active users for your organization</li>
            <li>All user roles count toward this limit — Owner, Admin, Manager, Contributor, Viewer, and Resource</li>
            <li>Starter allows up to 10 users, Professional up to 50, and Enterprise has no limit</li>
            <li>If you need more users than your current plan allows, upgrade to a higher tier</li>
          </ul>
        ),
      },
      {
        q: 'Which user roles count toward the user limit?',
        a: (
          <p className={s.answerP}>
            All active user roles count toward your plan's user limit, including Owner, Admin, Manager, Contributor, Viewer, and Resource users. For example, the Professional plan allows up to 50 active users across all roles. If you need to add more users, you can upgrade to the Enterprise plan for unlimited users.
          </p>
        ),
      },
      {
        q: 'What if I need more users than my plan allows?',
        a: (
          <p className={s.answerP}>
            If you need more users than your current plan allows, you can upgrade to a higher tier at any time. The Enterprise plan offers unlimited users, projects, and resources. Contact us at billing@resourceflow.com if you have questions about which plan is right for your team size.
          </p>
        ),
      },
      {
        q: 'Can I upgrade or downgrade my plan at any time?',
        a: (
          <ul className={s.answerList}>
            <li><strong>Upgrade:</strong> Takes effect immediately, with the price difference prorated for the remainder of your billing cycle</li>
            <li><strong>Downgrade:</strong> Scheduled for the start of your next billing cycle so you retain full access until then</li>
            <li>If a downgrade would put you below your current usage (e.g., fewer seats than active users), you will be guided to reduce usage first</li>
          </ul>
        ),
      },
      {
        q: 'Are there any hidden fees or setup costs?',
        a: (
          <p className={s.answerP}>
            No. The monthly plan price is all-inclusive. There are no setup fees, no per-project charges, and no per-user add-on costs. The price you see for each plan covers all users within that plan's user limit.
          </p>
        ),
      },
      {
        q: 'Do you offer annual billing or discounts?',
        a: (
          <p className={s.answerP}>
            Plans are billed monthly. If you are interested in annual billing with a discount, contact us at billing@resourceflow.com and we can arrange a custom annual subscription for your organization.
          </p>
        ),
      },
    ],
  },
  {
    title: 'Subscriptions & Billing',
    icon: '\uD83D\uDCDD',
    items: [
      {
        q: 'How do I upgrade my subscription plan?',
        a: (
          <ul className={s.answerList}>
            <li>Org Admins can upgrade from the subscription manager within the app</li>
            <li>Select a higher plan and complete the Stripe checkout</li>
            <li>Your plan and feature limits are updated immediately</li>
            <li>The price difference is prorated for the current billing cycle</li>
          </ul>
        ),
      },
      {
        q: 'What happens if I downgrade my plan?',
        a: (
          <ul className={s.answerList}>
            <li>Downgrades take effect at the start of your next billing cycle — you keep full access until then</li>
            <li>If the downgrade would reduce your limits below current usage, you will need to reduce usage first</li>
            <li>The app will guide you through the process</li>
          </ul>
        ),
      },
      {
        q: 'What is the subscription lifecycle?',
        a: (
          <ol className={s.answerSteps}>
            <li><strong>Trialing</strong> — 14-day free trial with full access</li>
            <li><strong>Active</strong> — Paid subscription in good standing</li>
            <li><strong>Past Due</strong> — Trial expired without payment</li>
            <li><strong>Grace Period</strong> — Payment failed, 14 days to resolve</li>
            <li><strong>Suspended</strong> — Organization suspended after grace period expires</li>
            <li><strong>Canceled</strong> — Subscription ended by the user</li>
          </ol>
        ),
      },
      {
        q: 'Can I cancel my subscription at any time?',
        a: (
          <ul className={s.answerList}>
            <li>Org Admins can cancel from the subscription manager</li>
            <li>Cancellation takes effect at the end of your current billing period — you retain full access until then</li>
            <li>After cancellation, your organization enters a 14-day read-only grace period before being suspended</li>
            <li>You can reactivate at any time during the grace period</li>
          </ul>
        ),
      },
      {
        q: 'What is the grace period and how long do I have to reactivate?',
        a: (
          <ul className={s.answerList}>
            <li>After cancellation or non-payment, your organization enters a 14-day read-only grace period</li>
            <li>During this time you can reactivate and regain full access with all data intact</li>
            <li>After the grace period expires, your organization is suspended</li>
            <li>We recommend exporting your data before suspension if you do not plan to reactivate</li>
          </ul>
        ),
      },
      {
        q: 'How do I reactivate a canceled or expired subscription?',
        a: (
          <ul className={s.answerList}>
            <li>Log in to your account — you will see a reactivation prompt if your subscription is in a grace or suspended state</li>
            <li>Select a plan and complete the Stripe checkout</li>
            <li>Your subscription returns to Active status with full access restored</li>
            <li>All your data is preserved during the grace period</li>
          </ul>
        ),
      },
      {
        q: 'Will I be notified before my subscription expires or is suspended?',
        a: (
          <ul className={s.answerList}>
            <li>Before your trial ends</li>
            <li>When a payment fails</li>
            <li>When the grace period begins</li>
            <li>Before data is permanently deleted</li>
          </ul>
        ),
        note: 'Make sure your account email and the billing contact email are current so you receive these alerts.',
      },
    ],
  },
  {
    title: 'Payments',
    icon: '\uD83D\uDCB3',
    items: [
      {
        q: 'How are payments processed?',
        a: (
          <ul className={s.answerList}>
            <li>All payments are processed securely through Stripe, a PCI-compliant payment provider</li>
            <li>AllocatIQ never sees or stores your full credit card number</li>
            <li>Your card information is tokenized and stored on Stripe's secure infrastructure</li>
          </ul>
        ),
      },
      {
        q: 'How do I update my payment method or credit card?',
        a: (
          <ul className={s.answerList}>
            <li>Org Admins can manage payment methods through the Stripe Customer Portal, accessible from the subscription manager</li>
            <li>Click "Manage payment methods" to open the portal</li>
            <li>Add a new card, set a default payment method, or remove an old one</li>
            <li>Changes take effect immediately for future billing</li>
          </ul>
        ),
      },
      {
        q: 'Will I receive invoices for my payments?',
        a: (
          <ul className={s.answerList}>
            <li>Stripe automatically generates invoices for each billing cycle</li>
            <li>View and download past invoices from the Stripe Customer Portal at any time</li>
            <li>Configure custom billing details (company name, tax ID, address) in the portal before invoices are generated</li>
          </ul>
        ),
      },
      {
        q: 'What happens if my payment fails?',
        a: (
          <ol className={s.answerSteps}>
            <li>Stripe automatically retries the card over several days</li>
            <li>We notify you by email so you can update your payment method</li>
            <li>During the retry period, your access continues normally</li>
            <li>If payment fails after retries, your account enters a 14-day grace period with read-only access before suspension</li>
          </ol>
        ),
      },
      {
        q: 'Do you offer refunds?',
        a: (
          <p className={s.answerP}>
            Subscription fees are non-refundable as stated in our Terms of Service. However, if you experience a billing error or are charged in error, contact billing@resourceflow.com and we will investigate and issue a correction if warranted.
          </p>
        ),
      },
      {
        q: 'How are billing disputes handled?',
        a: (
          <p className={s.answerP}>
            If you believe you have been charged incorrectly, contact billing@resourceflow.com with your organization name and the invoice in question. We will review the charge and respond within 5 business days. If an error is confirmed, we will issue a credit or refund for the disputed amount.
          </p>
        ),
      },
      {
        q: 'What taxes are applied to my subscription?',
        a: (
          <ul className={s.answerList}>
            <li>Subscription fees are billed in USD and are exclusive of applicable taxes</li>
            <li>Depending on your location, Stripe may add applicable sales tax, VAT, or GST at checkout</li>
            <li>Tax details are shown on your invoices, which you can configure in the Stripe Customer Portal</li>
          </ul>
        ),
      },
    ],
  },
  {
    title: 'Account & Access',
    icon: '\uD83D\uDC64',
    items: [
      {
        q: 'How do I sign up for an account?',
        a: (
          <ul className={s.answerList}>
            <li>Click "Start free trial" on the homepage</li>
            <li>Enter your name, email, and a password (at least 12 characters with a mix of uppercase, lowercase, numbers, and symbols)</li>
            <li>Complete a brief onboarding flow to set up your organization</li>
            <li>Your 14-day trial begins immediately</li>
          </ul>
        ),
      },
      {
        q: 'What user roles are available and what can each role do?',
        a: (
          <table className={s.answerTable}>
            <thead>
              <tr>
                <th>Role</th>
                <th>Access Level</th>
              </tr>
            </thead>
            <tbody>
              <tr><td className={s.answerTableLabel}><strong>Owner</strong></td><td>Full control, billing, can delete the organization</td></tr>
              <tr><td className={s.answerTableLabel}><strong>Admin</strong></td><td>Full control of users, settings, and all data</td></tr>
              <tr><td className={s.answerTableLabel}><strong>Manager</strong></td><td>Create and edit projects, allocate resources, approve timesheets</td></tr>
              <tr><td className={s.answerTableLabel}><strong>Contributor</strong></td><td>Limited editing access</td></tr>
              <tr><td className={s.answerTableLabel}><strong>Viewer</strong></td><td>Read-only access to dashboards and reports</td></tr>
              <tr><td className={s.answerTableLabel}><strong>Resource</strong></td><td>Log timesheets and view own assignments</td></tr>
            </tbody>
          </table>
        ),
      },
      {
        q: 'How do I invite team members to my organization?',
        a: (
          <ul className={s.answerList}>
            <li>Org Admins and Owners can invite members from the Team Management section</li>
            <li>Enter the person's email and assign a role</li>
            <li>The invitee receives an email with a link to accept the invitation and set up their account</li>
            <li>If they already have an AllocatIQ account, they are added directly to your organization upon acceptance</li>
          </ul>
        ),
      },
      {
        q: 'How does the invitation acceptance process work?',
        a: (
          <ul className={s.answerList}>
            <li>Invitees receive an email containing a secure invitation link</li>
            <li>Clicking the link takes them to a page where they can sign in with an existing account or create a new one</li>
            <li>Once authenticated, they are added to the inviting organization with the role specified in the invitation</li>
            <li>Invitation links expire after 7 days for security</li>
          </ul>
        ),
      },
      {
        q: 'Do I need to verify my email address?',
        a: (
          <ul className={s.answerList}>
            <li>After signing up, you receive a verification email</li>
            <li>Click the link in the email to confirm your address</li>
            <li>Email verification is required before you can fully access the platform</li>
            <li>If you did not receive the email, check your spam folder or use the resend link on the verification page</li>
          </ul>
        ),
      },
      {
        q: 'Does AllocatIQ support multi-factor authentication (MFA)?',
        a: (
          <p className={s.answerP}>
            Yes. MFA using time-based one-time passwords (such as Google Authenticator or Authy) is supported. You can enable MFA from your account settings. We strongly recommend enabling MFA for all admin and manager accounts to protect your organization's data.
          </p>
        ),
      },
      {
        q: 'What password requirements does AllocatIQ enforce?',
        a: (
          <ul className={s.answerList}>
            <li>At least 12 characters long</li>
            <li>At least one uppercase letter</li>
            <li>At least one lowercase letter</li>
            <li>At least one number</li>
            <li>At least one symbol</li>
          </ul>
        ),
        note: 'Passwords are securely hashed and never stored in plaintext.',
      },
    ],
  },
  {
    title: 'Security & Data Privacy',
    icon: '\uD83D\uDD12',
    items: [
      {
        q: 'How is my organization\'s data isolated from other customers?',
        a: (
          <p className={s.answerP}>
            Each organization's data is kept strictly separate from every other customer. Access is verified on every request, and no user can access data belonging to another organization. We do not disclose the specific isolation mechanisms publicly.
          </p>
        ),
      },
      {
        q: 'How are my passwords protected?',
        a: (
          <p className={s.answerP}>
            Passwords are protected using industry-standard one-way hashing. This means your password cannot be reverse-engineered or recovered from what we store. Passwords are never stored in plaintext.
          </p>
        ),
      },
      {
        q: 'How are login sessions secured?',
        a: (
          <ul className={s.answerList}>
            <li>Login sessions are secured and automatically expire after a set period</li>
            <li>Sessions are invalidated when you log out</li>
            <li>Repeated failed login attempts are limited to protect against brute-force attacks</li>
            <li>The platform is accessed over an encrypted connection</li>
          </ul>
        ),
      },
      {
        q: 'What does the audit trail record?',
        a: (
          <ul className={s.answerList}>
            <li>User creation and removal</li>
            <li>Role changes</li>
            <li>Subscription changes</li>
            <li>Project creation and deletion</li>
            <li>Allocation modifications</li>
          </ul>
        ),
        note: 'Each audit entry records who performed the action, what changed, when it happened, and the source. Audit logging is available on the Professional and Enterprise plans.',
      },
      {
        q: 'Who can see sensitive data in my organization?',
        a: (
          <ul className={s.answerList}>
            <li><strong>Admins &amp; Owners:</strong> Can see everything</li>
            <li><strong>Managers:</strong> Can see project, resource, and financial data for projects they manage</li>
            <li><strong>Viewers:</strong> See dashboards and reports but cannot edit</li>
            <li><strong>Resources:</strong> Can only see their own assignments and timesheets</li>
          </ul>
        ),
        note: 'You control exactly who has access to what.',
      },
      {
        q: 'Is my data encrypted?',
        a: (
          <p className={s.answerP}>
            All communication with the platform is encrypted in transit. We follow industry best practices for data protection. If you have specific encryption requirements for your organization, contact us to discuss your needs.
          </p>
        ),
      },
      {
        q: 'What happens to my data when I delete my account?',
        a: (
          <ul className={s.answerList}>
            <li>When you cancel your subscription, your data enters a 14-day read-only grace period</li>
            <li>During this time you can reactivate and regain full access</li>
            <li>After the grace period, your organization is suspended</li>
            <li>You can export your data at any time before suspension</li>
          </ul>
        ),
      },
      {
        q: 'Does AllocatIQ sell my data to third parties?',
        a: (
          <p className={s.answerP}>
            No. We do not sell your personal or organizational data to any third party. Your data is used solely to provide the AllocatIQ service. We share data only with trusted infrastructure providers (for hosting and payment processing) and when required by law. See our Privacy Policy for full details.
          </p>
        ),
      },
      {
        q: 'How does AllocatIQ protect against security threats?',
        a: (
          <p className={s.answerP}>
            We employ multiple layers of security including access controls, protection against common web vulnerabilities, secure password handling, and monitoring of sensitive actions. For security reasons, we do not disclose specific technical measures publicly.
          </p>
        ),
      },
    ],
  },
  {
    title: 'Compliance',
    icon: '\u2705',
    items: [
      {
        q: 'Is AllocatIQ working toward GDPR compliance?',
        a: (
          <p className={s.answerP}>
            We are working toward GDPR compliance. Our goal is to support your rights to access, correct, delete, export, and restrict processing of your personal data. To exercise any data rights, contact privacy@resourceflow.com and we will respond within 30 days. See our Privacy Policy for full details.
          </p>
        ),
      },
      {
        q: 'Is AllocatIQ working toward CCPA compliance?',
        a: (
          <p className={s.answerP}>
            We are working toward CCPA compliance. California residents can request to know what personal information is collected, request deletion, and opt out of the sale of personal information. We do not sell personal information. Contact privacy@resourceflow.com to exercise your CCPA rights.
          </p>
        ),
      },
      {
        q: 'What is your data retention policy?',
        a: (
          <ul className={s.answerList}>
            <li>User data is retained while your account is active</li>
            <li>After cancellation or non-payment, data is preserved during a 14-day grace period</li>
            <li>After the grace period, the organization is suspended</li>
            <li>We recommend exporting your data before suspension if you do not plan to reactivate</li>
          </ul>
        ),
        note: 'For specific data retention needs, contact us to discuss your requirements.',
      },
      {
        q: 'Do you support single sign-on (SSO)?',
        a: (
          <p className={s.answerP}>
            SSO is a planned feature for the Enterprise plan. Our goal is to support Azure AD (Microsoft Entra ID), Google Workspace, and Okta. SSO will centralize authentication through your identity provider, enforce corporate password and MFA policies, and simplify onboarding and offboarding. Contact us if you would like to be notified when SSO is available.
          </p>
        ),
      },
      {
        q: 'How long are audit logs retained?',
        a: (
          <p className={s.answerP}>
            Audit logs are retained while your organization is active. If you require a specific retention period or immutability guarantees for compliance purposes, contact us to discuss your needs.
          </p>
        ),
      },
      {
        q: 'Can I export my data for compliance or portability purposes?',
        a: (
          <ul className={s.answerList}>
            <li>Yes — you can export your data at any time from within the platform</li>
            <li>Data export is available in CSV format for resources, projects, allocations, timesheets, and reports</li>
            <li>This supports GDPR data portability requirements and allows you to maintain your own backups</li>
          </ul>
        ),
      },
      {
        q: 'Where is my data stored?',
        a: (
          <p className={s.answerP}>
            Your data is stored on secure cloud infrastructure. For specific data residency or infrastructure requirements, contact us to discuss available options.
          </p>
        ),
      },
    ],
  },
  {
    title: 'Support & Service Levels',
    icon: '\uD83C\uDF1F',
    items: [
      {
        q: 'What support is included with each plan?',
        a: (
          <table className={s.answerTable}>
            <thead>
              <tr>
                <th>Plan</th>
                <th>Support Level</th>
              </tr>
            </thead>
            <tbody>
              <tr><td className={s.answerTableLabel}><strong>Starter</strong></td><td>Email support with best-effort response times</td></tr>
              <tr><td className={s.answerTableLabel}><strong>Professional</strong></td><td>Priority email support</td></tr>
              <tr><td className={s.answerTableLabel}><strong>Enterprise</strong></td><td>Dedicated support with a dedicated account manager</td></tr>
            </tbody>
          </table>
        ),
        note: 'All plans include access to our in-app help resources and documentation.',
      },
      {
        q: 'How do I contact support?',
        a: (
          <table className={s.answerTable}>
            <thead>
              <tr>
                <th>Need</th>
                <th>Email</th>
              </tr>
            </thead>
            <tbody>
              <tr><td className={s.answerTableLabel}>General support</td><td>support@resourceflow.com</td></tr>
              <tr><td className={s.answerTableLabel}>Billing questions</td><td>billing@resourceflow.com</td></tr>
              <tr><td className={s.answerTableLabel}>Legal or compliance</td><td>legal@resourceflow.com</td></tr>
            </tbody>
          </table>
        ),
        note: 'Include your organization name and a description of the issue so we can help you as quickly as possible.',
      },
      {
        q: 'What are the expected response times?',
        a: (
          <p className={s.answerP}>
            We strive to respond to all support inquiries as quickly as possible. Professional plan customers receive priority handling over Starter. Enterprise customers receive the highest priority with a dedicated account manager. For mission-critical issues, we recommend the Enterprise plan. Contact us to discuss specific response time needs for your organization.
          </p>
        ),
      },
      {
        q: 'What uptime does AllocatIQ target?',
        a: (
          <p className={s.answerP}>
            We strive for high availability and perform scheduled maintenance during off-peak hours with advance notice whenever possible. We do not currently offer a formal uptime SLA with service credits. If uptime guarantees are important to your organization, contact us to discuss Enterprise options.
          </p>
        ),
      },
      {
        q: 'How do I report a bug or technical issue?',
        a: (
          <ul className={s.answerList}>
            <li>Email support@resourceflow.com with a description of the issue</li>
            <li>Include steps to reproduce it and any screenshots or error messages</li>
            <li>Include your organization name and the browser you are using</li>
            <li>Our team will investigate and respond according to your plan's support level</li>
          </ul>
        ),
      },
      {
        q: 'Is there a status page I can check during outages?',
        a: (
          <p className={s.answerP}>
            We are working toward a public status page. In the meantime, if you suspect an outage, email support@resourceflow.com and we will provide updates. For Enterprise customers, we provide proactive notifications during any service degradation.
          </p>
        ),
      },
      {
        q: 'Do you offer onboarding or training assistance?',
        a: (
          <p className={s.answerP}>
            The platform is designed to be self-serve with an intuitive interface and in-app guidance. For Enterprise customers, we offer personalized onboarding sessions and training for your team. Contact support@resourceflow.com if you would like to arrange a training session.
          </p>
        ),
      },
    ],
  },
]

function FAQAccordion({ category }: { category: FAQCategory }) {
  const [openIndex, setOpenIndex] = useState<number | null>(null)

  return (
    <div className={s.category}>
      <h2 className={s.categoryTitle}>
        <span className={s.categoryIcon} aria-hidden="true">{category.icon}</span>
        {category.title}
      </h2>
      <div className={s.itemList}>
        {category.items.map((item, i) => {
          const isOpen = openIndex === i
          const note = (item as FAQItem & { note?: string }).note
          return (
            <div key={i} className={`${s.item} ${isOpen ? s.itemOpen : ''}`}>
              <button
                className={s.question}
                onClick={() => setOpenIndex(isOpen ? null : i)}
                aria-expanded={isOpen}
                aria-controls={`faq-answer-${category.title}-${i}`}
              >
                <span className={s.questionText}>{item.q}</span>
                <span className={`${s.chevron} ${isOpen ? s.chevronOpen : ''}`} aria-hidden="true">
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <path d="M4 6L8 10L12 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </span>
              </button>
              <div
                id={`faq-answer-${category.title}-${i}`}
                className={s.answerWrapper}
                style={{ maxHeight: isOpen ? '800px' : '0' }}
              >
                <div className={s.answer}>
                  {item.a}
                  {note && <p className={s.answerNote}>{note}</p>}
                </div>
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

export default function FAQPage() {
  return (
    <div className={layout.page}>
      <nav className={layout.navbar}>
        <div className={layout.navInner}>
          <Link to="/" className={layout.navBrand}>
            <div className={layout.navLogo}>
              <svg viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg" width="20" height="20" aria-hidden="true">
                <path d="M10 26L18 10L26 26" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M13 21H23" stroke="white" strokeWidth="2.5" strokeLinecap="round"/>
              </svg>
            </div>
            <span className={layout.navName}>AllocatIQ</span>
          </Link>
          <ul className={layout.navLinks}>
            <li><a href="/#features">Features</a></li>
            <li><a href="/#pricing">Pricing</a></li>
            <li><Link to="/faq">FAQ</Link></li>
          </ul>
          <div className={layout.navActions}>
            <Link to="/login" className={layout.navSignIn}>Sign in</Link>
            <Link to="/signup" className={layout.navCta}>Start free trial</Link>
          </div>
        </div>
      </nav>

      <section className={s.hero}>
        <div className={s.heroInner}>
          <div className={s.heroBadge}>Frequently Asked Questions</div>
          <h1 className={s.heroTitle}>Answers to common questions about AllocatIQ</h1>
          <p className={s.heroSub}>
            Everything you need to know about features, pricing, billing, security, and support. Can't find what you're looking for?{' '}
            <Link to="/contact" className={s.heroLink}>Contact our team</Link>.
          </p>
        </div>
      </section>

      <section className={s.content}>
        <div className={s.contentInner}>
          {faqData.map((category) => (
            <FAQAccordion key={category.title} category={category} />
          ))}

          <div className={s.contactCta}>
            <h2 className={s.contactCtaTitle}>Still have questions?</h2>
            <p className={s.contactCtaText}>
              Our team is here to help. Reach out and we'll get back to you as quickly as possible.
            </p>
            <div className={s.contactCtaActions}>
              <Link to="/contact" className={s.contactCtaBtn}>Contact us</Link>
              <Link to="/signup" className={s.contactCtaBtnOutline}>Start free trial</Link>
            </div>
          </div>
        </div>
      </section>

      <footer className={layout.footer}>
        <div className={layout.footerInner}>
          <div className={layout.footerTop}>
            <div>
              <div className={layout.footerBrand}>
                <div className={layout.footerLogo}>
                  <svg viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg" width="20" height="20" aria-hidden="true">
                    <path d="M10 26L18 10L26 26" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M13 21H23" stroke="white" strokeWidth="2.5" strokeLinecap="round"/>
                  </svg>
                </div>
                <span className={layout.footerBrandName}>AllocatIQ</span>
              </div>
              <p className={layout.footerTagline}>Resource forecasting and utilization for professional services teams.</p>
            </div>
            <div>
              <div className={layout.footerColTitle}>Product</div>
              <ul className={layout.footerLinks}>
                <li><a href="/#features">Features</a></li>
                <li><a href="/#pricing">Pricing</a></li>
                <li><Link to="/faq">FAQ</Link></li>
                <li><Link to="/signup">Free trial</Link></li>
              </ul>
            </div>
            <div>
              <div className={layout.footerColTitle}>Company</div>
              <ul className={layout.footerLinks}>
                <li><Link to="/about">About</Link></li>
                <li><Link to="/contact">Contact</Link></li>
              </ul>
            </div>
            <div>
              <div className={layout.footerColTitle}>Legal</div>
              <ul className={layout.footerLinks}>
                <li><Link to="/privacy">Privacy Policy</Link></li>
                <li><Link to="/terms">Terms of Service</Link></li>
              </ul>
            </div>
          </div>
          <hr className={layout.footerDivider} />
          <div className={layout.footerBottom}>
            <span>&copy; 2026 AllocatIQ. All rights reserved.</span>
            <span>Made for professional services teams</span>
          </div>
        </div>
      </footer>
    </div>
  )
}
